import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import ItemCard from '../components/ItemCard';
import ModalConfirm from '../components/ModalConfirm';
import Modal from '../components/ui/Modal';
import Field, { FormRow } from '../components/ui/Field';
import Estado from '../components/ui/Estado';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { toast } from '../components/Toast';
import { fmtBRL, fmtData, hoje } from '../utils/format';

const FORM_VAZIO = {
  dataRecebimento: hoje(),
  pagador: '',
  valor: '',
  descricao: '',
};

function payloadDoForm(form) {
  return {
    dataRecebimento: form.dataRecebimento,
    pagador: form.pagador.trim(),
    valor: parseFloat(form.valor),
    descricao: form.descricao?.trim() || null,
  };
}

export default function ContasAReceber() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillAplicado = useRef(false);

  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [form, setForm] = useState({ ...FORM_VAZIO });
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [erroModal, setErroModal] = useState('');
  const [excluindo, setExcluindo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [filtro, setFiltro] = useState('PENDENTES');
  const [expandidaId, setExpandidaId] = useState(null);

  // Prefill vindo do cadastro de venda (?valor=&pagador=&descricao=).
  useEffect(() => {
    if (prefillAplicado.current) return;
    const valor = params.get('valor');
    const pagador = params.get('pagador');
    const descricao = params.get('descricao');
    if (!valor && !pagador && !descricao) return;
    prefillAplicado.current = true;
    setForm((f) => ({
      ...f,
      dataRecebimento: hoje(),
      valor: valor || f.valor,
      pagador: pagador || f.pagador,
      descricao: descricao || f.descricao,
    }));
    // O cadastro agora mora num modal, então quem chega pela venda precisa
    // encontrá-lo já aberto com os campos preenchidos.
    setCriando(true);
    navigate('/contas-a-receber', { replace: true });
  }, [params, navigate]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      setLista(await api.getContasAReceber() || []);
    } catch (err) {
      setErroCarga(err.message || 'Não foi possível carregar as contas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.criarContaAReceber(payloadDoForm(form));
      setForm({ ...FORM_VAZIO, dataRecebimento: hoje() });
      setCriando(false);
      toast('Conta a receber cadastrada.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const abrirEdicao = (c) => {
    setErroModal('');
    setEditando({
      id: c.id,
      dataRecebimento: (c.dataRecebimento || '').slice(0, 10),
      pagador: c.pagador || '',
      valor: String(c.valor ?? ''),
      descricao: c.descricao || '',
    });
  };

  const salvarEdicao = async (e) => {
    e?.preventDefault();
    setErroModal('');
    try {
      await api.editarContaAReceber(editando.id, payloadDoForm(editando));
      setEditando(null);
      toast('Conta atualizada.', 'success');
      carregar();
    } catch (err) {
      setErroModal(err.message);
    }
  };

  const marcarRecebido = async (c) => {
    try {
      await api.marcarContaRecebida(c.id);
      toast('Conta marcada como recebida.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  const reabrir = async (c) => {
    try {
      await api.reabrirContaAReceber(c.id);
      toast('Conta reaberta como pendente.', 'success');
      carregar();
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  const confirmarExclusao = async () => {
    try {
      await api.excluirContaAReceber(excluindo.id);
      setExcluindo(null);
      toast('Conta removida.', 'success');
      carregar();
    } catch (err) {
      setExcluindo(null);
      toast(err.message, 'error');
    }
  };

  const listaFiltrada = lista.filter((c) => {
    if (filtro === 'PENDENTES') return c.status === 'PENDENTE';
    if (filtro === 'RECEBIDAS') return c.status === 'RECEBIDO';
    return true;
  });

  const camposConta = (dados, aplicar) => (
    <>
      <FormRow>
        <Field label="Dia do recebimento" obrigatorio>
          <input
            type="date"
            value={dados.dataRecebimento}
            onChange={(e) => aplicar((x) => ({ ...x, dataRecebimento: e.target.value }))}
            required
          />
        </Field>
        <Field label="De quem irá receber" obrigatorio>
          <input
            value={dados.pagador}
            onChange={(e) => aplicar((x) => ({ ...x, pagador: e.target.value }))}
            required
            placeholder="Ex: Cooperativa, João, Armazém"
          />
        </Field>
        <Field label="Valor (R$)" obrigatorio>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={dados.valor}
            onChange={(e) => aplicar((x) => ({ ...x, valor: e.target.value }))}
            required
            placeholder="0,00"
          />
        </Field>
      </FormRow>
      <Field label="Descrição (opcional)">
        <input
          value={dados.descricao}
          onChange={(e) => aplicar((x) => ({ ...x, descricao: e.target.value }))}
          placeholder="Anotação sobre este recebimento"
          maxLength={500}
        />
      </Field>
    </>
  );

  return (
    <div>
      <PageHeader
        titulo="Contas a receber"
        icone="conta-receber"
        descricao="Quando e de quem você vai receber. Útil para vendas a prazo."
        acao={{ label: 'Nova conta', icone: 'adicionar', onClick: () => setCriando(true) }}
      />

      <div className="card">
        <div className="secao-titulo">
          <div className="chips">
            {[
              { id: 'PENDENTES', label: 'Pendentes' },
              { id: 'RECEBIDAS', label: 'Recebidas' },
              { id: 'TODAS', label: 'Todas' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                className={`chip ${filtro === f.id ? 'chip--ativo' : ''}`}
                onClick={() => setFiltro(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {lista.length > 0 && <span className="contador">{listaFiltrada.length}</span>}
        </div>

        <Estado carregando={carregando} erro={erroCarga} aoTentarNovamente={carregar} itens={3}>
          {listaFiltrada.length === 0 ? (
            <EmptyState
              compacto
              icone="conta-receber"
              titulo={filtro === 'RECEBIDAS' ? 'Nenhuma conta recebida' : 'Nenhuma conta a receber'}
              descricao="Cadastre um recebimento futuro, ou gere um a partir de uma venda."
              acao={filtro === 'RECEBIDAS' ? undefined : { label: 'Nova conta', onClick: () => setCriando(true) }}
            />
          ) : (
            <div className="lista-cards">
              {listaFiltrada.map((c) => {
            const recebida = c.status === 'RECEBIDO';
            const aberta = expandidaId === c.id;
            const dias = (() => {
              if (!c.dataRecebimento || recebida) return null;
              const hojeD = new Date(`${hoje()}T12:00:00`);
              const data = new Date(`${c.dataRecebimento.slice(0, 10)}T12:00:00`);
              return Math.round((data - hojeD) / 86400000);
            })();
            return (
              <div key={c.id} className="item-expansivel">
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={aberta}
                  onClick={() => setExpandidaId(aberta ? null : c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandidaId(aberta ? null : c.id);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <ItemCard
                    onEdit={recebida ? undefined : () => abrirEdicao(c)}
                    onDelete={() => setExcluindo(c)}
                    actions={(
                      <button
                        type="button"
                        className="item-card-btn"
                        onClick={() => (recebida ? reabrir(c) : marcarRecebido(c))}
                      >
                        {recebida ? 'Reabrir' : 'Marcar recebida'}
                      </button>
                    )}
                  >
                    <div className="item-conteudo">
                      <div className="item-titulo">{c.pagador}</div>
                      <div className="item-meta">Receber em {fmtData(c.dataRecebimento)}</div>
                      {!recebida && dias != null && (
                        <div className={`item-nota ${dias < 0 ? 'item-nota--atraso' : dias <= 2 ? 'item-nota--proximo' : ''}`}>
                          {dias < 0 ? `Atrasada ${Math.abs(dias)} dia(s)` : dias === 0 ? 'Receber hoje' : `Em ${dias} dia(s)`}
                        </div>
                      )}
                      {recebida && c.recebidoEm && (
                        <div className="item-nota item-nota--ok">Recebida em {fmtData(c.recebidoEm)}</div>
                      )}
                    </div>
                    <div className="item-valor">{fmtBRL(c.valor)}</div>
                    <span className="item-expandir" aria-hidden="true">
                      <Icon nome={aberta ? 'chevron-cima' : 'chevron-baixo'} tamanho={16} />
                    </span>
                  </ItemCard>
                </div>

                {aberta && (
                  <div className="painel-detalhe">
                    <div className="painel-detalhe__rotulo">Descrição</div>
                    <div>{c.descricao?.trim() ? c.descricao : '—'}</div>
                  </div>
                )}
              </div>
            );
              })}
            </div>
          )}
        </Estado>
      </div>

      {criando && (
        <Modal titulo="Nova conta a receber" onClose={() => setCriando(false)} largura={560}>
          <form onSubmit={salvar}>
            {camposConta(form, setForm)}
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setCriando(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Cadastrar conta'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editando && (
        <Modal titulo="Editar conta a receber" onClose={() => setEditando(null)} largura={560}>
          {erroModal && <div className="aviso aviso--erro">{erroModal}</div>}
          <form onSubmit={salvarEdicao}>
            {camposConta(editando, setEditando)}
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setEditando(null)}>Cancelar</button>
              <button type="submit" className="btn-primary">Salvar</button>
            </div>
          </form>
        </Modal>
      )}

      {excluindo && (
        <ModalConfirm
          titulo="Excluir conta"
          mensagem={<>Excluir conta de <strong>{excluindo.pagador}</strong> ({fmtBRL(excluindo.valor)})?</>}
          onConfirm={confirmarExclusao}
          onCancel={() => setExcluindo(null)}
        />
      )}
    </div>
  );
}
